import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Typography,
} from '@mui/joy';
import type { CatalogProduct } from '../../lib/distributorsApi';

interface Props {
  open: boolean;
  product: CatalogProduct | null;
  onClose: () => void;
  onConfirm: (line: { uom: string; quantity: number; length_value?: number; length_uom?: string }) => void;
}

/**
 * UOM / quantity / length picker shown when an item needs choices before it
 * can enter the cart. Dimensional items REQUIRE a length matching one of the
 * catalog's variations — free-text lengths would fail the whole ABC order.
 */
export default function CatalogItemAddModal({ open, product, onClose, onConfirm }: Props) {
  const [uom, setUom] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [variationIndex, setVariationIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!product) return;
    const stocking = product.uoms.find((entry) => entry.is_stocking) || product.uoms[0];
    setUom(stocking?.code || 'EA');
    setQuantity(1);
    setVariationIndex(product.dimension_options.length === 1 ? 0 : null);
  }, [product]);

  if (!product) return null;

  const needsLength = product.is_dimensional;
  const canConfirm = Number.isSafeInteger(quantity) && quantity >= 1 && !!uom && (!needsLength || variationIndex !== null);

  const handleConfirm = () => {
    const variation = variationIndex !== null ? product.dimension_options[variationIndex] : null;
    onConfirm({
      uom,
      quantity,
      ...(variation ? { length_value: variation.value, length_uom: variation.uom } : {}),
    });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog data-sentry-block sx={{ minWidth: { xs: 0, sm: 360 }, width: { xs: 'calc(100% - 32px)', sm: 'auto' }, maxWidth: 720 }}>
        <Typography level="title-lg">{product.description}</Typography>
        <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
          {product.item_number}
          {product.supplier_name ? ` · ${product.supplier_name}` : ''}
        </Typography>

        <FormControl sx={{ mb: 1.5 }}>
          <FormLabel>Unit of measure</FormLabel>
          <Select value={uom} onChange={(_event, value) => value && setUom(value)}>
            {product.uoms.map((entry) => (
              <Option key={entry.code} value={entry.code}>
                {entry.code} — {entry.name}
                {entry.is_stocking ? ' (stocking)' : ''}
              </Option>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ mb: 1.5 }}>
          <FormLabel>Quantity</FormLabel>
          <Input
            type="number"
            value={quantity}
            slotProps={{ input: { min: 1 } }}
            onChange={(event) => setQuantity(Math.max(1, parseInt(event.target.value, 10) || 1))}
          />
        </FormControl>

        {needsLength && (
          <FormControl sx={{ mb: 1.5 }} error={variationIndex === null}>
            <FormLabel>Length</FormLabel>
            <Select
              value={variationIndex}
              placeholder="Pick a length…"
              onChange={(_event, value) => setVariationIndex(value as number | null)}
            >
              {product.dimension_options.map((variation, index) => (
                <Option key={`${variation.value}-${variation.uom}`} value={index}>
                  {variation.size || `${variation.value} ${variation.uom}`}
                </Option>
              ))}
            </Select>
            <FormHelperText>
              ABC prices this item by length — pick one of the available cuts.
            </FormHelperText>
          </FormControl>
        )}

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 1 }}>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!canConfirm} onClick={handleConfirm}>
            Add to cart
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
